import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import * as Sentry from "@sentry/react";
import { AppDispatch } from "../store";
import {
  APIGiftTable,
  GiftTableApiError,
  updateGiftTable as updateGiftTableApi,
  getGiftTable as getGiftTableApi,
  publishGiftTable as publishGiftTableApi,
  choosePlan as choosePlanApi,
  getGiftTableStatus as getGiftTableStatusApi,
} from "../../services/giftTableApiService";
import { GatewayTokenRestoreError } from "../../services/gatewaySession";

interface EventLocation {
  address: string;
  lat: number;
  lng: number;
}

export interface GiftTableProduct {
  productID: string;
  category: string;
  categoryID: string;
  brand: string;
  quantity: number;
  taken: number;
  priority: boolean;
  value: number;
  isPhysical?: boolean;
}

export interface GiftEntry {
  giftID: string;
  seen: boolean;
}

export interface Organizer {
  firstName: string;
  lastName: string;
}

export interface GiftTableState {
  ID: string;
  amphitryon: string;
  eventName: string;
  eventType: string;
  eventDate: string;
  eventLocation: EventLocation;
  googleMapsLink: string;
  eventPlace: string;
  messageToGuests: string;
  organizers: Organizer[];
  theme: string;
  profilePicture: string;
  thumbnailSmall?: string;
  thumbnailMed?: string;
  thumbnailLg?: string;
  status: string;
  plan: string;
  dueDate: string;
  closed: boolean;
  closedAt: string;
  products: GiftTableProduct[];
  totalValue?: number;
  // collectedValue?: number;
  // gifts?: GiftEntry[];
  retentionFeeRate?: number;
  slug: string;
  // retentionValue?: number;
}

export const createBaseGiftTableState = (): GiftTableState => ({
  ID: "",
  amphitryon: "",
  eventName: "",
  eventType: "",
  eventDate: "",
  eventLocation: {
    address: "",
    lat: 0,
    lng: 0,
  },
  googleMapsLink: "",
  eventPlace: "",
  messageToGuests: "",
  organizers: [],
  theme: "",
  profilePicture: "",
  thumbnailSmall: "",
  thumbnailMed: "",
  thumbnailLg: "",
  status: "",
  plan: "",
  dueDate: "",
  closed: false,
  closedAt: "",
  products: [],
  totalValue: undefined,
  retentionFeeRate: undefined,
  slug: "",
});

export const mergeGiftTableState = (
  state: Partial<GiftTableState>
): GiftTableState => {
  const base = createBaseGiftTableState();
  return {
    ...base,
    ...state,
    eventLocation: {
      ...base.eventLocation,
      ...(state.eventLocation ?? {}),
    },
    products: state.products ?? base.products,
    organizers: state.organizers ?? base.organizers,
    slug: state.slug ?? base.slug,
  };
};

const giftTableSlice = createSlice({
  name: "giftTable",
  initialState: createBaseGiftTableState(),
  reducers: {
    loadGiftTableState: (_state, action: PayloadAction<GiftTableState>) => {
      const newState = mergeGiftTableState(action.payload);
      localStorage.setItem("giftTableState", JSON.stringify(newState));
      if (newState.theme) {
        localStorage.setItem("theme", newState.theme);
      }
      return newState;
    },
    updateGiftTableState: (
      state,
      action: PayloadAction<Partial<GiftTableState>>
    ) => {
      const newState = mergeGiftTableState({ ...state, ...action.payload });
      localStorage.setItem("giftTableState", JSON.stringify(newState));
      if (newState.theme) {
        localStorage.setItem("theme", newState.theme);
      }
      return newState;
    },
    clearGiftTableState: () => {
      localStorage.removeItem("giftTableState");
      localStorage.removeItem("theme");
      return createBaseGiftTableState();
    },
    addProductToGiftTableState: (
      state,
      action: PayloadAction<GiftTableProduct>
    ) => {
      const existingProduct = state.products?.find(
        (product) => product.productID === action.payload.productID
      );
      if (existingProduct) {
        existingProduct.quantity += action.payload.quantity;
        existingProduct.priority = action.payload.priority;
        existingProduct.value = action.payload.value;
        existingProduct.isPhysical =
          action.payload.isPhysical ?? existingProduct.isPhysical;
      } else {
        if (!state.products) {
          state.products = [];
        }
        state.products.push(action.payload);
      }
      localStorage.setItem("giftTableState", JSON.stringify(state));
    },
    removeProductFromGiftTableState: (state, action: PayloadAction<string>) => {
      state.products = state.products.filter(
        (product) => product.productID !== action.payload
      );
      localStorage.setItem("giftTableState", JSON.stringify(state));
    },
    updateProductQuantityState: (
      state,
      action: PayloadAction<{ productID: string; quantity: number }>
    ) => {
      const product = state.products.find(
        (product) => product.productID === action.payload.productID
      );
      if (product) {
        product.quantity = action.payload.quantity;
        localStorage.setItem("giftTableState", JSON.stringify(state));
      }
    },
    setGiftTableValues: (
      state,
      action: PayloadAction<{
        totalValue: number;
        // collectedValue: number;
        plan: string;
        dueDate: string;
        status: string;
      }>
    ) => {
      state.totalValue = action.payload.totalValue;
      // state.collectedValue = action.payload.collectedValue;
      state.plan = action.payload.plan;
      state.dueDate = action.payload.dueDate;
      state.status = action.payload.status;
      localStorage.setItem("giftTableState", JSON.stringify(state));
    },
    setGiftTableStatus: (state, action: PayloadAction<string>) => {
      state.status = action.payload;
      localStorage.setItem("giftTableState", JSON.stringify(state));
    },
  },
});

export const {
  loadGiftTableState,
  updateGiftTableState,
  clearGiftTableState,
  addProductToGiftTableState,
  removeProductFromGiftTableState,
  updateProductQuantityState,
  setGiftTableValues,
  setGiftTableStatus,
} = giftTableSlice.actions;

export default giftTableSlice.reducer;

// Helper function to map GiftTableState to GiftTable
const mapGiftTableStateToGiftTable = (
  state: GiftTableState
): Partial<APIGiftTable> => {
  return {
    id: state.ID,
    amphitryon: state.amphitryon,
    event_name: state.eventName,
    event_type: state.eventType,
    event_date: state.eventDate,
    event_location: {
      address: state.eventLocation.address,
      lat: state.eventLocation.lat,
      lng: state.eventLocation.lng,
    },
    event_place: state.eventPlace,
    organizers: state.organizers?.map((organizer) => ({
      first_name: organizer.firstName,
      last_name: organizer.lastName,
    })),
    profile_picture: state.profilePicture,
    thumbnail_small: state.thumbnailSmall,
    thumbnail_med: state.thumbnailMed,
    thumbnail_lg: state.thumbnailLg,
    products: state.products?.map((product) => ({
      product_id: product.productID,
      category: product.category,
      category_id: product.categoryID,
      brand: product.brand,
      quantity: product.quantity,
      taken: product.taken,
      priority: product.priority,
      is_physical: product.isPhysical || false,
      value: product.value,
    })),
    // gifts: state.gifts?.map((gift) => ({
    //   gift_id: gift.giftID,
    //   seen: gift.seen,
    // })),
    slug: state.slug,
  };
};

// Thunk actions to sync with the backend and fetch updated values
type GiftTableSyncOperation =
  | "update"
  | "clear"
  | "add_product"
  | "remove_product"
  | "update_quantity"
  | "publish"
  | "choose_plan";

export const syncGiftTableWithBackend =
  (operation: GiftTableSyncOperation = "update") =>
  async (
    dispatch: AppDispatch,
    getState: () => { giftTable: GiftTableState }
  ) => {
    const giftTableState = getState().giftTable;
    const giftTable = mapGiftTableStateToGiftTable(giftTableState);
    let stage: "save" | "reload_after_save" = "save";
    Sentry.addBreadcrumb({
      category: "my_table.sync",
      message: "Saving gift table changes",
      level: "info",
      data: { operation },
    });
    try {
      await updateGiftTableApi(giftTable);

      stage = "reload_after_save";
      const updatedGiftTable = await getGiftTableApi(giftTableState.ID);
      dispatch(
        setGiftTableValues({
          totalValue: updatedGiftTable.total_value!,
          // collectedValue: updatedGiftTable.collected_value!,
          plan: updatedGiftTable.plan!,
          dueDate: updatedGiftTable.due_date,
          status: updatedGiftTable.status,
        })
      );
      Sentry.addBreadcrumb({
        category: "my_table.sync",
        message: "Gift table changes saved and reloaded",
        level: "info",
        data: { operation },
      });
    } catch (error) {
      const failureStage = error instanceof GatewayTokenRestoreError
        ? error.stage
        : stage;
      const httpStatus = error instanceof GatewayTokenRestoreError || error instanceof GiftTableApiError
        ? error.httpStatus
        : undefined;
      const appTokenAgeSeconds = error instanceof GatewayTokenRestoreError
        ? error.appTokenAgeSeconds
        : undefined;
      const appTokenExpired = error instanceof GatewayTokenRestoreError
        ? error.appTokenExpired
        : undefined;
      const failureKind = error instanceof GatewayTokenRestoreError
        ? "session_restore"
        : stage === "save"
          ? "gift_table_save"
          : "gift_table_reload";

      Sentry.addBreadcrumb({
        category: "my_table.sync",
        message: "Gift table synchronization failed",
        level: "error",
        data: { operation, failureKind, failureStage, httpStatus: httpStatus ?? null },
      });
      Sentry.withScope((scope) => {
        scope.setTag("feature", "my_table");
        scope.setTag("operation", operation);
        scope.setTag("failure_kind", failureKind);
        scope.setTag("failure_stage", failureStage);
        if (httpStatus !== undefined) scope.setTag("http.status_code", String(httpStatus));
        scope.setContext("gift_table_sync", {
          operation,
          failureKind,
          failureStage,
          httpStatus: httpStatus ?? null,
          appTokenAgeSeconds: appTokenAgeSeconds ?? null,
          appTokenExpired: appTokenExpired ?? null,
        });
        Sentry.captureException(new Error(`My table ${operation} sync failed`));
      });
      console.error("Error syncing gift table with backend", {
        operation,
        failureKind,
        failureStage,
        httpStatus: httpStatus ?? null,
        appTokenAgeSeconds: appTokenAgeSeconds ?? null,
        appTokenExpired: appTokenExpired ?? null,
      });
    }
  };

export const fetchAndLoadGiftTable =
  (tableID: string) => async (dispatch: AppDispatch) => {
    try {
      const activeGiftTable = await getGiftTableApi(tableID);

      const giftTableState: GiftTableState = {
        ID: activeGiftTable.id!,
        amphitryon: activeGiftTable.amphitryon,
        eventName: activeGiftTable.event_name,
        eventType: activeGiftTable.event_type,
        eventDate: activeGiftTable.event_date,
        eventLocation: {
          address: activeGiftTable.event_location.address,
          lat: activeGiftTable.event_location.lat,
          lng: activeGiftTable.event_location.lng,
        },
        eventPlace: activeGiftTable.event_place,
        messageToGuests: activeGiftTable.message_to_guests,
        theme: activeGiftTable.theme,
        organizers:
          activeGiftTable.organizers?.map((organizer) => ({
            firstName: organizer.first_name,
            lastName: organizer.last_name,
          })) || [],
        profilePicture: activeGiftTable.profile_picture,
        thumbnailSmall: activeGiftTable.thumbnail_small,
        thumbnailMed: activeGiftTable.thumbnail_med,
        thumbnailLg: activeGiftTable.thumbnail_lg,
        status: activeGiftTable.status,
        plan: activeGiftTable.plan,
        dueDate: activeGiftTable.due_date,
        closed: activeGiftTable.closed,
        closedAt: activeGiftTable.closed_at,
        products: activeGiftTable.products?.map((product) => ({
          productID: product.product_id,
          quantity: product.quantity,
          category: product.category,
          categoryID: product.category_id,
          brand: product.brand,
          taken: product.taken,
          priority: product.priority,
          value: product.value,
          isPhysical: product.is_physical,
        })),
        totalValue: activeGiftTable.total_value,
        // collectedValue: activeGiftTable.collected_value,
        // gifts: activeGiftTable.gifts?.map((gift) => ({
        //   giftID: gift.gift_id,
        //   seen: gift.seen,
        // })),
        googleMapsLink: activeGiftTable.google_maps_link,
        retentionFeeRate: activeGiftTable.retention_fee_rate,
        slug: activeGiftTable.slug || "",
        // retentionValue: activeGiftTable.retention_value,
      };

      dispatch(loadGiftTableState(giftTableState));
    } catch (error) {
      console.error("Error fetching and loading gift table", error);
    }
  };

export const updateGiftTable =
  (payload: Partial<GiftTableState>) => (dispatch: AppDispatch) => {
    dispatch(updateGiftTableState(payload));
    dispatch(syncGiftTableWithBackend("update"));
  };

export const clearGiftTable = () => (dispatch: AppDispatch) => {
  dispatch(clearGiftTableState());
  dispatch(syncGiftTableWithBackend("clear"));
};

export const addProductToGiftTable =
  (product: GiftTableProduct) => (dispatch: AppDispatch) => {
    dispatch(addProductToGiftTableState(product));
    dispatch(syncGiftTableWithBackend("add_product"));
  };

export const removeProductFromGiftTable =
  (productID: string) => (dispatch: AppDispatch) => {
    dispatch(removeProductFromGiftTableState(productID));
    dispatch(syncGiftTableWithBackend("remove_product"));
  };

export const updateProductQuantity =
  (payload: { productID: string; quantity: number }) =>
  (dispatch: AppDispatch) => {
    dispatch(updateProductQuantityState(payload));
    dispatch(syncGiftTableWithBackend("update_quantity"));
  };

export const publishGiftTable =
  (tableID: string) => async (dispatch: AppDispatch) => {
    try {
      await publishGiftTableApi(tableID);
      dispatch(syncGiftTableWithBackend("publish"));
    } catch (error) {
      console.error("Error publishing gift table", error);
    }
  };

export const choosePlan =
  (tableID: string, plan: string) => async (dispatch: AppDispatch) => {
    try {
      await choosePlanApi(tableID, plan);
      dispatch(syncGiftTableWithBackend("choose_plan"));
    } catch (error) {
      console.error("Error choosing plan", error);
    }
  };

export const fetchGiftTableStatus =
  (tableID: string) => async (dispatch: AppDispatch) => {
    try {
      const status = await getGiftTableStatusApi(tableID);
      dispatch(setGiftTableStatus(status));
    } catch (error) {
      console.error("Error fetching gift table status", error);
    }
  };
